/**
 * The video file, made on the visitor's device: the same timeline and renderFrame as
 * the player, stepped frame by frame and encoded with WebCodecs (via Mediabunny).
 * No server, no storage.
 */
import { type ImageAssets, urlsOf } from "./assets";
import { workCanvas } from "./canvas";
import { musicGain, soundWindows } from "./music";
import { renderFrame } from "./render";
import { mediaOf, segmentAt } from "./timeline";
import type {
  AssetSource,
  ClipSlide,
  Drawable,
  Segment,
  Timeline,
} from "./types";

const FPS = 30;
const SAMPLE_RATE = 48_000;
/** How finely the music's volume curve is sampled into the film. */
const GAIN_SAMPLES_PER_SECOND = 50;
const AUDIO_BITRATE = 128_000;
/**
 * The film is drawn on the page's own thread, beside the player. Work this long, then give
 * the player its next frame: counted in frames, a 1080p phone froze the player for 0.3 s.
 */
const SLICE_MS = 12;

/**
 * Waits once SLICE_MS of work has passed: until the next frame is painted, or a moment
 * on a hidden page (it paints nothing; abortWhenHidden stops that film anyway).
 */
function pacer() {
  let slice = performance.now();
  return async (onPause: () => void) => {
    if (performance.now() - slice <= SLICE_MS) {
      return;
    }
    onPause();
    await new Promise<void>((resolve) => {
      if (document.hidden) {
        setTimeout(resolve, 0);
        return;
      }
      // Hidden mid-wait, no frame comes: go on, and the film is stopped as hidden at once.
      const done = () => {
        document.removeEventListener("visibilitychange", done);
        resolve();
      };
      document.addEventListener("visibilitychange", done);
      requestAnimationFrame(() => setTimeout(done, 0));
    });
    slice = performance.now();
  };
}

export type EncodeFailure = "unsupported" | "aborted" | "hidden" | "failed";

export class EncodeError extends Error {
  readonly reason: EncodeFailure;

  constructor(reason: EncodeFailure, message?: string) {
    super(message ?? reason);
    this.reason = reason;
  }
}

export interface EncodeOptions {
  assets: ImageAssets;
  height: number;
  musicUrl: string | null;
  onProgress?: (fraction: number) => void;
  reducedMotion?: boolean;
  signal?: AbortSignal;
  timeline: Timeline;
  width: number;
}

export interface EncodeResult {
  audioCodec: string | null;
  blob: Blob;
  duration: number;
  extension: "mp4" | "webm";
  mimeType: string;
  seconds: number;
  videoCodec: string;
}

/** Whether this browser can make the file at all (no WebCodecs: share the link instead). */
export function canEncodeVideo(): boolean {
  return (
    typeof window !== "undefined" &&
    "VideoEncoder" in window &&
    "VideoFrame" in window &&
    typeof OfflineAudioContext !== "undefined"
  );
}

const loadMediabunny = () => import("mediabunny");
type Mediabunny = Awaited<ReturnType<typeof loadMediabunny>>;
type Output = InstanceType<Mediabunny["Output"]>;
type BufferTarget = InstanceType<Mediabunny["BufferTarget"]>;

async function decode(
  oc: OfflineAudioContext,
  url: string,
  signal?: AbortSignal
) {
  const res = await fetch(url, { signal });
  return oc.decodeAudioData(await res.arrayBuffer());
}

/** The music with its fades and dips, and any clip that keeps its sound: the same curve as the page. */
async function mixAudio(
  timeline: Timeline,
  musicUrl: string | null,
  signal?: AbortSignal
): Promise<AudioBuffer | null> {
  const T = timeline.duration;
  const windows = soundWindows(timeline);
  if (!(musicUrl || windows.length)) {
    return null;
  }
  const oc = new OfflineAudioContext(
    2,
    Math.ceil(T * SAMPLE_RATE),
    SAMPLE_RATE
  );

  if (musicUrl) {
    const node = oc.createBufferSource();
    const gain = oc.createGain();
    node.buffer = await decode(oc, musicUrl, signal);
    node.loop = true;
    // The page's own volume curve, sampled: the file sounds exactly as the player does.
    const steps = Math.max(2, Math.ceil(T * GAIN_SAMPLES_PER_SECOND));
    const curve = new Float32Array(steps);
    for (let i = 0; i < steps; i += 1) {
      curve[i] = musicGain(timeline, (i / (steps - 1)) * T, false);
    }
    gain.gain.setValueCurveAtTime(curve, 0, T);
    node.connect(gain).connect(oc.destination);
    node.start(0);
  }

  const withSound = timeline.segments.filter(
    (seg) => seg.slide.kind === "clip" && seg.slide.sound
  );
  await Promise.all(
    withSound.map(async (seg) => {
      const clip = seg.slide as ClipSlide;
      try {
        const node = oc.createBufferSource();
        node.buffer = await decode(oc, clip.src, signal);
        node.connect(oc.destination);
        node.start(seg.start, 0, clip.duration);
      } catch {
        // A clip whose sound can't be decoded plays silent; the music still dips.
      }
    })
  );

  return oc.startRendering();
}

interface Decoded {
  canvas: Drawable;
  timestamp: number;
}

/** Decodes each clip frame by frame, in order, so the file matches the page exactly. */
class ClipFrames {
  protected readonly M: Mediabunny;
  protected states = new Map<
    string,
    { iter: AsyncGenerator<Decoded>; cur: Decoded | null; next: Decoded | null }
  >();
  protected frames = new Map<string, Drawable | null>();
  /** Clips this device can't decode (say an HEVC .mov): they show their poster instead. */
  protected broken = new Set<string>();

  constructor(M: Mediabunny) {
    this.M = M;
  }

  protected async open(slide: ClipSlide) {
    const input = new this.M.Input({
      formats: this.M.ALL_FORMATS,
      source: new this.M.UrlSource(slide.src),
    });
    const track = await input.getPrimaryVideoTrack();
    if (!track) {
      return null;
    }
    const iter = new this.M.CanvasSink(track).canvases(
      0
    ) as AsyncGenerator<Decoded>;
    const first = await iter.next();
    const state = { cur: null, iter, next: first.done ? null : first.value };
    this.states.set(slide.id, state);
    return state;
  }

  /** One clip that fails to decode shows its poster; it never stops the whole film. */
  async advance(slide: ClipSlide, lt: number) {
    if (this.broken.has(slide.id)) {
      return;
    }
    try {
      const st = this.states.get(slide.id) ?? (await this.open(slide));
      if (!st) {
        this.frames.set(slide.id, null);
        return;
      }
      while (st.next && st.next.timestamp <= lt + 1e-3) {
        st.cur = st.next;
        // biome-ignore lint/performance/noAwaitInLoops: a decoder hands out frames one at a time, in order.
        const r = await st.iter.next();
        st.next = r.done ? null : r.value;
      }
      this.frames.set(slide.id, (st.cur ?? st.next)?.canvas ?? null);
    } catch {
      this.broken.add(slide.id);
      this.frames.set(slide.id, null);
    }
  }

  get(slide: ClipSlide) {
    return this.frames.get(slide.id) ?? null;
  }

  async close() {
    await Promise.all(
      Array.from(this.states.values()).map((st) => st.iter.return?.(undefined))
    );
  }
}

/** The clips showing at time t (this slide and the one it transitions from). */
function clipsAt(timeline: Timeline, t: number): [ClipSlide, Segment][] {
  const k = segmentAt(timeline, t);
  const out: [ClipSlide, Segment][] = [];
  for (const seg of [timeline.segments[k], timeline.segments[k - 1]]) {
    const slide = seg ? mediaOf(seg.slide) : null;
    if (seg && slide?.kind === "clip") {
      out.push([slide, seg]);
    }
  }
  return out;
}

interface Pipeline {
  audioCodec: string | null;
  isMp4: boolean;
  output: Output;
  video: InstanceType<Mediabunny["CanvasSource"]>;
  videoCodec: string;
}

async function makePipeline(
  M: Mediabunny,
  o: EncodeOptions,
  canvas: HTMLCanvasElement
): Promise<Pipeline> {
  const { width: w, height: h } = o;
  const codec = await M.getFirstEncodableVideoCodec(["avc", "vp9", "av1"], {
    height: h,
    width: w,
  });
  if (!codec) {
    throw new EncodeError("unsupported", "no video codec for this size");
  }
  const isMp4 = codec === "avc";
  // fastStart puts the index first, which share sheets and social apps expect.
  const format = isMp4
    ? new M.Mp4OutputFormat({ fastStart: "in-memory" })
    : new M.WebMOutputFormat();
  const output = new M.Output({ format, target: new M.BufferTarget() });
  // The film moves on almost every frame (drift, prints landing, crossfades): a variable
  // bitrate spends little on still frames and what movement needs.
  const video = new M.CanvasSource(canvas, {
    bitrate: new M.Quality({ bitrateMode: "variable", quality: "high" }),
    codec,
  });
  output.addVideoTrack(video, { frameRate: FPS });

  let audioCodec: string | null = null;
  try {
    const allowed = format
      .getSupportedAudioCodecs()
      .filter((c) => c === "aac" || c === "opus");
    audioCodec = await M.getFirstEncodableAudioCodec(allowed, {
      numberOfChannels: 2,
      sampleRate: SAMPLE_RATE,
    });
  } catch {
    audioCodec = null;
  }
  return { audioCodec, isMp4, output, video, videoCodec: codec };
}

export async function encodeVideo(o: EncodeOptions): Promise<EncodeResult> {
  if (!canEncodeVideo()) {
    throw new EncodeError("unsupported");
  }
  const { timeline, width: w, height: h, assets, signal } = o;
  const check = () => {
    if (signal?.aborted) {
      throw new EncodeError(signal.reason === "hidden" ? "hidden" : "aborted");
    }
  };

  const M = await loadMediabunny();
  check();

  // Everything the film shows must be loaded before the first frame is drawn.
  await assets.preload(urlsOf(timeline.segments.map((s) => s.slide)), 6);
  await document.fonts?.ready;
  check();

  const canvas = workCanvas(w, h);
  const scratch = workCanvas(w, h);
  try {
    return await drawFilm(o, M, check, canvas, scratch);
  } finally {
    canvas.remove();
    scratch.remove();
  }
}

/** The film drawn frame by frame on `canvas` and encoded. */
async function drawFilm(
  o: EncodeOptions,
  M: Mediabunny,
  check: () => void,
  canvas: HTMLCanvasElement,
  scratch: HTMLCanvasElement
): Promise<EncodeResult> {
  const { timeline, width: w, height: h, assets, signal } = o;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new EncodeError("unsupported", "no canvas");
  }
  const { output, video, audioCodec, isMp4, videoCodec } = await makePipeline(
    M,
    o,
    canvas
  );

  // No AAC/Opus encoder (or the music failed to load): the film is made without sound.
  let audio: AudioBuffer | null = null;
  let audioSource: InstanceType<Mediabunny["AudioBufferSource"]> | null = null;
  if (audioCodec) {
    audio = await mixAudio(timeline, o.musicUrl, signal).catch(() => null);
    if (audio) {
      audioSource = new M.AudioBufferSource({
        bitrate: AUDIO_BITRATE,
        codec: audioCodec as "aac" | "opus",
      });
      output.addAudioTrack(audioSource);
    }
  }
  check();

  const clips = new ClipFrames(M);
  // Photos come from the shared cache; clip frames from the decoder, never the page's <video>.
  const source: AssetSource = {
    blur: (src) => assets.blur(src),
    clipFrame: (slide) => clips.get(slide),
    image: (src) => assets.image(src),
  };
  const n = Math.ceil(timeline.duration * FPS);
  const t0 = performance.now();
  const pace = pacer();
  let finished = false;

  try {
    await output.start();
    if (audio && audioSource) {
      await audioSource.add(audio);
    }
    for (let f = 0; f < n; f += 1) {
      check();
      const t = f / FPS;
      for (const [clip, seg] of clipsAt(timeline, t)) {
        // biome-ignore lint/performance/noAwaitInLoops: each frame needs its clip frame before it is drawn.
        await clips.advance(
          clip,
          Math.max(0, Math.min(t - seg.start, clip.duration - 0.05))
        );
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      renderFrame(ctx, timeline, t, {
        assets: source,
        captions: true,
        height: h,
        pixelRatio: 1,
        reducedMotion: o.reducedMotion,
        scratch,
        shareSafe: true,
        width: w,
      });
      await video.add(t, 1 / FPS);
      await pace(() => o.onProgress?.(f / n));
    }
    check();
    await output.finalize();
    finished = true;
  } finally {
    await clips.close();
    if (!finished) {
      await output.cancel().catch(() => undefined);
    }
  }

  const { buffer } = output.target as BufferTarget;
  if (!buffer || buffer.byteLength < 1024) {
    throw new EncodeError("failed", "empty file");
  }
  o.onProgress?.(1);
  const mimeType = isMp4 ? "video/mp4" : "video/webm";
  return {
    audioCodec: audio ? audioCodec : null,
    blob: new Blob([buffer], { type: mimeType }),
    duration: timeline.duration,
    extension: isMp4 ? "mp4" : "webm",
    mimeType,
    seconds: (performance.now() - t0) / 1000,
    videoCodec,
  };
}

/**
 * Abort the encoding when the tab goes to the background: a throttled or suspended
 * tab must never hand over a broken file. The caller offers to start again.
 */
export function abortWhenHidden(controller: AbortController): () => void {
  const onChange = () => {
    if (document.hidden) {
      controller.abort("hidden");
    }
  };
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}
